import { useCallback, useMemo, useState } from 'react'

import { Icon } from '@circlefin/components'
import { usePermission } from '@circlefin/permissions/react'
import {
  BURN_FEE_V2_ENABLED,
  ROLR_REDEMPTION_ONLY_ACCOUNT,
} from '@services/permissions'
import { feeHistorySection } from '@services/sections/lib/billing'
import { TransWithLink } from '@shared/components/common'
import { ComplexTable, TableCard } from '@shared/components/tables'
import { Currency, useFeeReportsQuery } from '@shared/graphql'
import Link from 'next/link'
import useTranslation from 'next-translate/useTranslation'

import { FundAccountForBurnFee } from '../../modals'

import { useDailyRedemptionFeeTableColumns } from './DailyRedemptionFeeTable.Columns'

import type { FeeReport } from '@shared/graphql'

const { Header, Title, Body } = TableCard

export interface DailyRedemptionFeeTableProps {
  /**
   * Value used in fetching the fee reports. Used to enable story testing.
   */
  from?: Date
}

export const DailyRedemptionFeeTable: React.FC<
  DailyRedemptionFeeTableProps
> = ({ from }) => {
  const columns = useDailyRedemptionFeeTableColumns()
  const { t } = useTranslation('custody/common')
  const [, { isAuthorized: burnFeeV2Enabled }] =
    usePermission(BURN_FEE_V2_ENABLED)
  const [, { isAuthorized: isRolr }] = usePermission(
    ROLR_REDEMPTION_ONLY_ACCOUNT,
  )
  const [showFundAccountModal, setShowFundAccountModal] = useState(false)

  const startOfCurrentBurnPeriod = useMemo(() => new Date(), [])

  const { loading, data, refetch, error } = useFeeReportsQuery({
    variables: {
      page: { pageSize: 2, from: from ?? startOfCurrentBurnPeriod },
    },
    fetchPolicy: 'cache-and-network',
  })

  const sortedReports = useMemo(() => {
    if (data?.feeReports.list == null) return { list: [] }
    return {
      ...data.feeReports,
      list: data.feeReports.list.toSorted((a) =>
        a.fee.currency === Currency.USDC ? -1 : 1,
      ),
    }
  }, [data?.feeReports])

  const shouldShowTable = useMemo(() => {
    return burnFeeV2Enabled && !isRolr
  }, [burnFeeV2Enabled, isRolr])

  const handleSeeHereClicked = useCallback(() => {
    setShowFundAccountModal(true)
  }, [])

  const handleModalCloseEnd = useCallback(() => {
    setShowFundAccountModal(false)
  }, [])

  if (!shouldShowTable) return null

  return (
    <TableCard paddingVariant="none" disableCardAppearance>
      <Header>
        <div className="flex flex-row justify-between gap-2">
          <div className="flex flex-col gap-2">
            <Title>{t('dailyRedemptionFeeTable.header.title')}</Title>
            <span className="ml-5">
              <TransWithLink
                components={{
                  learn: <a onClick={handleSeeHereClicked} />,
                }}
                i18nKey="custody/common:dailyRedemptionFeeTable.header.subtitle"
                variant="support"
              />
            </span>
          </div>
          <Link href={{ pathname: feeHistorySection.route }}>
            <span className="flex h-10 w-44 items-center gap-1">
              {t('dailyRedemptionFeeTable.header.viewFeeHistory')}
              <Icon name="ArrowRightSolid" />
            </span>
          </Link>
        </div>
      </Header>
      <Body>
        <FundAccountForBurnFee
          onCloseEnd={handleModalCloseEnd}
          open={showFundAccountModal}
        />
        <ComplexTable<FeeReport>
          columns={columns}
          data={sortedReports}
          density="tight"
          empty={{
            title: t('dailyRedemptionFeeTable.emptyState.title'),
            subtitle: t('dailyRedemptionFeeTable.emptyState.subtitle'),
          }}
          enableSorting={false}
          error={error}
          loading={loading}
          numLoadingRows={2}
          retry={refetch}
        />
      </Body>
    </TableCard>
  )
}
